import React from 'react';
import styled, { css } from 'styled-components';
import { layout, flexbox } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { Icon } from '../icon/Icon';

const Content = styled.div`
  ${flexbox}
  display: flex;
  margin-bottom: 8px;
`;

const LabelWrap = styled.div`
  ${layout}
  display: inline-block;
  padding-bottom: 4px;
  font-size: ${themeGet('form.label.fontSize')};
  color: ${themeGet('form.label.color')};
  white-space: nowrap;
  line-height: 1.5;

  ${props =>
    props.required &&
    css`
      &::before {
        content: '* ';
        color: ${themeGet('form.label.requiredColor')};
      }
    `}
`;

const InputWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputIconWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  margin-left: 4px;
  align-items: center;
`;

const ErrorMessage = styled.div`
  min-height: ${themeGet('form.errorMessage.minHeight')};
  font-size: ${themeGet('form.errorMessage.fontSize')};
  color: ${themeGet('form.errorMessage.color')};
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
  margin-top: 4px;
`;

const statusColorMapping = {
  success: 'success',
  error: 'danger',
};

export const FormItem = ({ label, meta, input, children, required, labelWidth, validateStatus, ...rest }) => (
  <Content {...rest}>
    <LabelWrap required={required} width={labelWidth}>
      <label htmlFor={input.name}>{label}</label>
    </LabelWrap>
    <InputWrap>
      <InputIconWrap>
        {React.cloneElement(children, {
          id: input.name,
          error: meta.touched && (meta.error || meta.submitError),
        })}
        {validateStatus && (
          <IconWrapper>
            <Icon
              name={validateStatus && validateStatus.toUpperCase()}
              color={statusColorMapping[validateStatus]}
              fontSize={20}
            />
          </IconWrapper>
        )}
      </InputIconWrap>
      <ErrorMessage visible={meta.touched}>{meta.error || meta.submitError}</ErrorMessage>
    </InputWrap>
  </Content>
);

FormItem.defaultProps = {
  meta: {},
  input: {},
  flexDirection: 'column',
};

const VERTICAL = 'vertical';
const HORIZONTAL = 'horizontal';
const INLINE = 'inline';

export const Form = styled.form`
  display: block;
  ${layout}

  ${props =>
    props.layout === HORIZONTAL &&
    css`
      ${Content} {
        flex-direction: row;
        justify-content: flex-end;
        align-items: baseline;
      }
      ${LabelWrap} {
        flex-grow: 0;
        text-align: right;
        overflow: hidden;
        margin-right: 8px;
        white-space: normal;
      }

      ${InputWrap} {
        flex-grow: 1;
      }
    `}

  ${props =>
    props.layout === INLINE &&
    css`
      display: flex;
      ${Content} {
        flex-direction: row;
        align-items: baseline;
        margin-right: 16px;
        margin-bottom: 0;
      }

      ${LabelWrap} {
        margin-right: 8px;
      }
    `}
`;

Form.defaultProps = {
  layout: VERTICAL,
};
